import React from 'react';
import withLayout from 'hocs/withLayout';
import withRequiresAuth from 'hocs/withRequiresAuth';
import { useApolloClient, useMutation } from '@apollo/client/react';
import AccountSettings from './AccountSettings';
import { PageProps } from 'types/page';
import { Formik, FormikHelpers } from 'formik';
import { object as yupObject, string as yupString } from 'yup';
import {
  EMAIL_EXISTS,
  MY_USER,
  USERNAME_EXISTS,
  USERNAME_OR_EMAIL_EXISTS,
} from 'lib/graphql/queries/user';
import { reportError } from 'lib/errors';
import { UPDATE_MY_PROFILE } from 'lib/graphql/mutations/user';
import { useSnackbar } from 'notistack';
import { useAuth, UpdateAccountInput, UsernameOrEmailExistsArgs } from '@elevator/ui';

/**
 * Props for the AccountSettings index component
 */
type AccountSettingsIndexProps = PageProps;

/**
 * Response type for uniqueness check queries
 */
type UniquenessCheckResponse = {
  emailUsed?: boolean;
  usernameUsed?: boolean;
};

/**
 * AccountSettings Index Component
 *
 * Wrapper component that handles form state management, validation, and submission
 * for account settings. Wraps the AccountSettings presentation component with
 * Formik provider and handles GraphQL mutations.
 *
 * Features:
 * - Form validation using Yup schema
 * - Uniqueness checking for email and username
 * - Profile update mutation
 * - Error handling and user feedback
 * - Tracks touched fields to only update changed values
 *
 * @param props - Component props
 * @returns AccountSettings form wrapped in Formik provider
 */
const Index = (props: AccountSettingsIndexProps): React.JSX.Element => {
  const { currentUser, hydrateUser } = useAuth();

  const [touchedFields, setTouchedFields] = React.useState<string[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  const apolloClient = useApolloClient();

  /**
   * Checks if email or username is already in use
   *
   * @param args - Arguments containing email and/or username to check
   * @param query - GraphQL query to use (defaults to USERNAME_OR_EMAIL_EXISTS)
   * @returns Response indicating if email/username is used, or defaults to both true on error
   */
  const checkUniqueness = async (
    args: UsernameOrEmailExistsArgs,
    query = USERNAME_OR_EMAIL_EXISTS,
  ): Promise<UniquenessCheckResponse> => {
    try {
      const { data, error } = await apolloClient.query<UniquenessCheckResponse>({
        query,
        variables: args,
        fetchPolicy: 'network-only',
      });

      if (error) {
        // On GraphQL errors, assume both are used to prevent overwriting
        return {
          emailUsed: true,
          usernameUsed: true,
        };
      }

      return data || { emailUsed: false, usernameUsed: false };
    } catch (err) {
      reportError(err, {
        metaData: {
          page: 'Account Settings',
          operation: 'query userExists',
          arguments: args,
        },
        user: {
          username: currentUser.username,
          preferredUsername: currentUser.preferredUsername,
          email: currentUser.email,
        },
      });
      // On error, assume both are used to prevent overwriting
      return {
        emailUsed: true,
        usernameUsed: true,
      };
    }
  };

  const [updateMyProfile] = useMutation<any>(UPDATE_MY_PROFILE);

  /**
   * Handles form submission for account settings update
   *
   * Only updates fields that have been touched/changed. Validates uniqueness
   * of email and username before submitting. Shows success/error notifications.
   *
   * @param values - Form values from Formik
   * @param actions - Formik helpers for form state management
   */
  const handleSubmit = async (
    values: UpdateAccountInput,
    actions: FormikHelpers<UpdateAccountInput>,
  ): Promise<void> => {
    const updates: Partial<UpdateAccountInput> = {};

    touchedFields.forEach((field) => {
      const fieldKey = field as keyof UpdateAccountInput;
      updates[fieldKey] = values[fieldKey];
    });

    let invalidInput = false;

    if (updates.email && updates.preferredUsername) {
      const { emailUsed, usernameUsed } = await checkUniqueness({
        email: updates.email,
        username: updates.preferredUsername,
      });

      if (emailUsed || usernameUsed) {
        if (emailUsed) actions.setFieldError('email', 'This email is already in use');

        if (usernameUsed)
          actions.setFieldError('preferredUsername', 'This username is already in use');

        invalidInput = true;
      }
    } else if (updates.email) {
      const { emailUsed } = await checkUniqueness(
        {
          email: updates.email,
        },
        EMAIL_EXISTS,
      );

      if (emailUsed) {
        actions.setFieldError('email', 'This email is already in use');
        invalidInput = true;
      }
    } else if (updates.preferredUsername) {
      const { usernameUsed } = await checkUniqueness(
        {
          username: updates.preferredUsername,
        },
        USERNAME_EXISTS,
      );

      if (usernameUsed) {
        actions.setFieldError('preferredUsername', 'This username is already in use');
        invalidInput = true;
      }
    }

    if (invalidInput) {
      actions.setSubmitting(false);
      return;
    }

    try {
      await updateMyProfile({
        variables: { input: updates },
        refetchQueries: [{ query: MY_USER }],
        awaitRefetchQueries: true,
      });

      await hydrateUser();
      actions.setSubmitting(false);
      actions.resetForm();

      enqueueSnackbar(`Account Settings Updated`, {
        variant: 'info',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    } catch (err) {
      actions.setSubmitting(false);
      reportError(err, {
        metaData: {
          page: 'Account Settings',
          operation: 'mutation updateMyProfile',
          arguments: { input: updates },
        },
        user: {
          username: currentUser.username,
          email: currentUser.email,
        },
      });

      enqueueSnackbar(`Error Saving Profile`, {
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
  };

  /**
   * Basic form validation schema. This Yup object is used by Formik to
   * validate all fields in the form as well as provide adequate help text
   * when necessary.
   */
  const validationSchema = yupObject({
    firstName: yupString().required('This field is required'),
    lastName: yupString().required('This field is required'),
    email: yupString()
      .email()
      .test('noWhiteSpace', 'Emails cannot contain spaces', (value) => {
        return !/\s/.test(value);
      })
      .required('This field is required'),
    preferredUsername: yupString()
      .test('noWhiteSpace', 'Usernames cannot contain spaces', (value) => {
        return !/\s/.test(value);
      })
      .required('This field is required'),
  });

  // Base form defaults used by formik. These fields are NOT UPDATED IN STATE.
  // These fields are accessible via the onSubmit function handler for the form.
  const initialValues: UpdateAccountInput = {
    firstName: currentUser.firstName,
    lastName: currentUser.lastName,
    email: currentUser.email,
    preferredUsername: currentUser.preferredUsername,
  };

  /**
   * Handles tracking of touched fields to determine which fields to update
   *
   * Adds field to touched list if value changed from initial, removes if reverted.
   *
   * @param field - Field name to track
   * @param value - Current field value
   */
  const handleTouchedFields = (field: string, value: string): void => {
    const fieldKey = field as keyof UpdateAccountInput;
    const isTouched = touchedFields.includes(field);
    const hasChanged = value !== initialValues[fieldKey];

    if (!isTouched && hasChanged) {
      // Field changed from initial - add to touched list
      setTouchedFields((prev) => [...prev, field]);
    } else if (isTouched && !hasChanged) {
      // Field reverted to initial - remove from touched list
      setTouchedFields((prev) => prev.filter((touchedField) => touchedField !== field));
    }
  };

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      <AccountSettings
        {...props}
        handleBlur={handleTouchedFields}
        currentUser={currentUser}
      />
    </Formik>
  );
};

// Wrap with withRequiresAuth to ensure user is authenticated before rendering
// Then wrap with withLayout for consistent page layout
export default withLayout(withRequiresAuth(Index), {
  margin: true,
  contentWidth: 'sm',
});
